import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { NavigateFunction } from 'react-router-dom';
import { resolveApplyPromoEventDisplayName } from 'pi-kiosk-shared';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../app/pwa/customerFormDirtyGate.js';
import { Button } from '../../shared/ui/Button.js';
import { FormField } from '../../shared/ui/FormField.js';
import { useCustomerFormatters } from '../../shared/hooks/useCustomerFormatters.js';
import { parkPaymentResumeReturnTarget } from '../../shared/session/returnTarget.js';
import { resolveCustomerSignInPath } from '../auth/customerAuthPaths.js';
import { navigateToShopEntry } from '../hub/customerHubPaths.js';
import {
  readLastKnownSalesPointId,
  readLastKnownSalesPointSlug} from '../../shared/session/lastKnownSalesPoint.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useCheckoutTypedPromoCode } from './hooks/useCheckoutTypedPromoCode.js';
import {
  consumePromoCodeDraft,
  savePromoCodeDraft} from './promoCodeDraftStorage.js';
import { formatPromoCodeApplyErrorText } from './promoCodeApplyErrors.js';

function formatAppliedMagnitude(input: {
  readonly discountAmount: number | null;
  readonly discountBps: number | null;
  readonly formatPrice: (amountMajor: number) => string;
}): string {
  if (input.discountAmount != null && input.discountAmount > 0) {
    return input.formatPrice(input.discountAmount);
  }
  if (input.discountBps != null && input.discountBps > 0) {
    const percent = input.discountBps / 100;
    const trimmed =
      Number.isInteger(percent) ? String(percent) : percent.toFixed(2).replace(/\.?0+$/, '');
    return `${trimmed}%`;
  }
  return '';
}

export function CheckoutTypedPromoCodeBlock(input: {
  readonly checkoutSessionPublicId: string | null;
  readonly previewNetworkFailed: boolean;
  readonly tenantCode: string;
  readonly locationPathAndSearch: string;
  readonly navigate: NavigateFunction;
  /** Session charge currency for discount formatting. */
  readonly currency?: string;
  /** Plan §3.9 — HARD_HELD / INITIATED payment locks apply/remove. */
  readonly paymentHoldActive?: boolean;
  readonly promoClaimReleased?: boolean;
  /** SP green-light — when false, apply disabled with proactive copy (kiosk parity). */
  readonly promotionsProgramEnabled: boolean;
}): JSX.Element | null {
  const { t, i18n } = useTranslation('customer');
  const { formatPrice: formatPriceRaw } = useCustomerFormatters();
  const formatPrice = (amountMajor: number): string =>
    formatPriceRaw(amountMajor, input.currency);
  const { session } = useCustomerSession();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const paymentHoldActive = input.paymentHoldActive === true;
  const promotionsProgramEnabled = input.promotionsProgramEnabled === true;
  const promo = useCheckoutTypedPromoCode({
    checkoutSessionPublicId: input.checkoutSessionPublicId,
    previewNetworkFailed: input.previewNetworkFailed,
    paymentHoldActive,
    promoClaimReleased: input.promoClaimReleased,
    promotionsProgramEnabled,
  });

  useEffect(() => {
    if (promo.errorMapping != null && inputRef.current != null) {
      inputRef.current.focus();
    }
  }, [promo.errorMapping]);

  const { lastRecovery, clearError } = promo;

  useEffect(() => {
    if (lastRecovery === 'restart_checkout') {
      clearError();
      navigateToShopEntry({
        tenantCode: input.tenantCode,
        session,
        navigate: input.navigate,
        rememberedSalesPointSlug: readLastKnownSalesPointSlug(input.tenantCode),
        rememberedSalesPointId: readLastKnownSalesPointId(input.tenantCode)});
    } else if (lastRecovery === 'sign_in') {
      const sessionId = input.checkoutSessionPublicId?.trim() ?? '';
      if (sessionId.length > 0 && promo.code.trim().length > 0) {
        savePromoCodeDraft({
          tenantCode: input.tenantCode,
          checkoutSessionPublicId: sessionId,
          code: promo.code});
      }
      // G1 — navigate only after elevate park succeeds; keep recovery on null.
      const parked = parkPaymentResumeReturnTarget(input.locationPathAndSearch, {
        tenantCode: input.tenantCode,
        source: 'checkout',
      });
      if (parked === null) {
        return;
      }
      clearError();
      // Path-tenant sign-in so per-tenant checkout park is consumed (G4).
      input.navigate(resolveCustomerSignInPath(input.tenantCode));
    }
    // cancel_payment: keep the hold-active message visible — user cancels QR/payment first.
  }, [
    clearError,
    session,
    input,
    lastRecovery,
    promo.code,
  ]);

  // P1-60 — after sign-in return, auto re-apply draft once when session present.
  const autoReapplyDoneRef = useRef(false);
  useEffect(() => {
    if (autoReapplyDoneRef.current) {
      return;
    }
    if (promo.applied != null || paymentHoldActive) {
      return;
    }
    if (session == null) {
      return;
    }
    const sessionId = input.checkoutSessionPublicId?.trim() ?? '';
    if (sessionId.length === 0) {
      return;
    }
    const draft = consumePromoCodeDraft({
      tenantCode: input.tenantCode,
      checkoutSessionPublicId: sessionId});
    if (draft == null) {
      return;
    }
    autoReapplyDoneRef.current = true;
    void promo.applyCode(draft);
  }, [
    session,
    input.checkoutSessionPublicId,
    input.tenantCode,
    paymentHoldActive,
    promo,
  ]);

  const magnitude =
    promo.applied != null
      ? formatAppliedMagnitude({
          discountAmount: promo.applied.discountAmount,
          discountBps: promo.applied.discountBps,
          formatPrice})
      : '';

  const successLabel =
    promo.applied != null
      ? t('promo.codeEntry.applied', {
          eventName: resolveApplyPromoEventDisplayName(
            promo.applied,
            i18n.resolvedLanguage ?? i18n.language,
          ),
          magnitude})
      : null;

  const showLockBanner =
    paymentHoldActive ||
    promo.lastRecovery === 'cancel_payment' ||
    promo.errorMapping?.recovery === 'cancel_payment';

  const promoErrorText = ((): string | undefined => {
    if (promo.errorMapping != null) {
      return formatPromoCodeApplyErrorText(promo.errorMapping, (key) => t(key));
    }
    if (!promotionsProgramEnabled && promo.applied == null) {
      return t('promo.codeEntry.salesPointNotAllowed');
    }
    return undefined;
  })();

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="checkout-typed-promo-code"
      aria-live="polite"
      {...{
        [CUSTOMER_FORM_DIRTY_ATTR]:
          promo.applied == null && promo.code.trim().length > 0 ? 'true' : 'false'}}
    >
      {showLockBanner ? (
        <div
          className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-3"
          role="status"
          data-testid="checkout-promo-cart-locked"
        >
          <p className="text-sm text-[var(--color-on-surface)]">
            {t('promo.codeEntry.cartLockedCancelPayment')}
          </p>
        </div>
      ) : null}
      {promo.applied != null ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-[var(--color-on-surface)]">
            {t('promo.codeEntry.label')}
          </p>
          <p
            id="checkout-promo-code-success"
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)]"
            data-testid="checkout-promo-code-success"
          >
            {successLabel}
          </p>
          <Button
            intent="secondary"
            size="md"
            type="button"
            className="min-h-11 shrink-0 sm:max-w-xs"
            disabled={promo.removeDisabled}
            loading={promo.isRemoving}
            data-testid="checkout-promo-code-remove"
            onClick={() => {
              void promo.removeCode();
            }}
          >
            {t('promo.codeEntry.remove')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <FormField
              ref={inputRef}
              id="checkout-promo-code-input"
              label={t('promo.codeEntry.label')}
              type="text"
              autoComplete="off"
              spellCheck={false}
              disabled={!promo.fieldEnabled}
              placeholder={t('promo.codeEntry.placeholder')}
              value={promo.code}
              // G12 (b) — single-field promo: errorText-alone (no FormErrorSummary).
              errorText={promoErrorText}
              data-testid="checkout-promo-code-input"
              onChange={(event) => promo.setCode(event.target.value)}
            />
          </div>
          <Button
            intent="secondary"
            size="md"
            type="button"
            className="min-h-11 shrink-0 sm:min-w-[7rem]"
            disabled={promo.applyDisabled}
            loading={promo.isApplying}
            data-testid="checkout-promo-code-apply"
            onClick={() => {
              void promo.applyCode();
            }}
          >
            {t('promo.codeEntry.apply')}
          </Button>
        </div>
      )}
      {promo.errorMapping != null && promo.applied != null ? (
        <p
          id="checkout-promo-code-error"
          className="text-sm text-[var(--color-danger)]"
          role="alert"
          tabIndex={-1}
          data-testid="checkout-promo-code-error"
        >
          {t(promo.errorMapping.messageKey)}
          {promo.errorMapping.recoveryMessageKey != null
            ? ` ${t(promo.errorMapping.recoveryMessageKey)}`
            : ''}
        </p>
      ) : null}
    </div>
  );
}
